import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserGroupUser, fetchUserGroupsUsers } from 'common/services/api/UserGroupsUsers/userGroupsUsers.thunk';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { selectFilters } from './userGroupsUsersSlice';

type UserGroupUsersDeleteDialogContentItem = {
    firstName: string,
    lastName: string,
    userGroupId: string
};

export function UserGroupUsersDeleteDialogContent({
    itemId = '',
    editingRow,
    customDeleteTitle,
    paginationInfo
}: Readonly<DeleteDialogContentProps<UserGroupUsersDeleteDialogContentItem>>) {
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const pageInfo = useAppSelector(selectPageInfo);
    const filterProp = useAppSelector(selectFilters);
    const params = useParams();
    const tenantId = params.tenantId;
    const { firstName = '', lastName = '', userGroupId = '' } = editingRow ?? {};
    const userName = `${firstName} ${lastName}`;
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const { rowsOnPage } = paginationInfo ?? {};
    const { currentPage = 0, rowsPerPage = 0, sortModel } = paginationGridData ?? {};
    const { keywords } = filterProp;

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('User has been removed from this User Group under this Tenant.', { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const userGroupDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteUserGroupUser({
                userGroupId,
                userId: itemId.toString(),
                tenantId: tenantId ?? '',
                skip: (rowsOnPage === 1) && (currentPage > 0) ? ((currentPage - 1) * rowsPerPage) : (currentPage * rowsPerPage),
                take: rowsPerPage,
                search: keywords[0],
                sortKey: sortModel?.[0]?.field,
                sortOrder: sortModel?.[0]?.sort,
                status: filterProp.dropdowns.status.keys
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setPaginationGridData({
                        rows: result.rows,
                        rowsCount: result.count,
                        currentPage: result.skipped / rowsPerPage,
                        rowsPerPage,
                        sortModel
                    }));
                    successHandler();
                })
                .catch(error => {
                    const statusCode = extractStatusCode(error.message);
                    if (statusCode !== 404) {
                        enqueueSnackbar(
                            'There was an error while removing this User from this User Group.' +
                            DEFAULT_ERROR_MESSAGE,
                            { variant: 'error' }
                        );
                        return;
                    }
                    if (currentPage === 0) {
                        dispatch(setPaginationGridData({}));
                        successHandler();
                        return;
                    }
                    const requestParams = {
                        tenantId: tenantId ?? '',
                        usergroupId: userGroupId ?? '',
                        skip: 0,
                        take: rowsPerPage,
                        search: keywords[0],
                        sortKey: sortModel?.[0]?.field,
                        sortOrder: sortModel?.[0]?.sort,
                        status: filterProp.dropdowns.status.keys
                    };
                    dispatch(fetchUserGroupsUsers(requestParams))
                        .unwrap()
                        .then((result) => {
                            dispatch(setPaginationGridData({
                                rows: result.data,
                                rowsCount: result.count,
                                currentPage: result.skipped / rowsPerPage,
                                rowsPerPage,
                                sortModel,
                            }));
                        })
                        .catch(() => {
                            dispatch(setPaginationGridData({}));
                        })
                        .finally(() => {
                            successHandler();
                        });
                })
                .finally(() => {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return (
        <DeleteDialog deleteCallback={userGroupDeleteCallback} processing={processing ?? false} customDeleteTitle={customDeleteTitle}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User: <Typography component="span" color="text.primary" fontWeight="400">{userName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User Group Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.tenantName}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
